////////////////////////////////////////////
////////////////////////////////////////////
// Imports (module imports)

import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import ControlPanel from '../features/controls/ControlPanel';
import Legend from '../features/legend/Legend';
import './NetworkGraph.css';
import {
  buildColorMaps,
  createNodePathInfo,
  getNodeColor as getNodeColorFromMaps
} from '../features/network-graph/transforms/colors';
import { buildGroups as buildGroupsFromData } from '../features/network-graph/transforms/groups';
import {
  clampNodeCenterToMovableDisk as clampNodeToDisk,
  clampNodesInPlace as clampNodesToDisk,
  linkPath as computeLinkPath,
  arrowPathAtFraction
} from '../features/network-graph/transforms/geometry';
import { renderClusterContents as renderClusterContentsFromModule } from '../features/network-graph/render/clusters';
////////////////////////////////////////////
////////////////////////////////////////////


const NODE_RADIUS = 30;

/////////////////////////////////////////////
/////////////////////////////////////////////
// buildGroups function (build groups)

function renderNodeVisual(nodeGroup, d, nodePathInfo, options) {
  const {
    colorMaps,
    colorBy,
    getNodeColor,
    includeHub = false,
    includeDataAttrs = false,
    simplified = false
  } = options;

  if (!simplified && nodePathInfo) {
    const items = nodePathInfo.items;
    const colorMap = colorMaps[colorBy];
    const anglePerItem = (2 * Math.PI) / items.length;

    items.forEach((item, i) => {
      const startAngle = i * anglePerItem;
      const endAngle = (i + 1) * anglePerItem;
      const path = nodeGroup
        .append('path')
        .attr('d', d3.arc().innerRadius(0).outerRadius(NODE_RADIUS).startAngle(startAngle).endAngle(endAngle))
        .attr('fill', colorMap[item] || '#9e9e9e');
      if (includeDataAttrs) path.attr('data-slice', item);
    });
  } else {
    const circle = nodeGroup.append('circle').attr('r', NODE_RADIUS).attr('fill', getNodeColor(d)).attr('stroke', 'none');
    if (includeDataAttrs) circle.attr('data-single', true);
  }

  if (includeHub && !simplified) {
    nodeGroup.append('circle').attr('class', 'node-hub').attr('r', 6);
  }
}

//////////////////////////////////////////////////////
//////////////////////////////////////////////////////


// Define zoom settings (split mobile vs desktop).
// Values below default to the existing behavior; tweak separately as needed.
const ZOOM_MIN_DESKTOP = 0.03;
const ZOOM_MAX_DESKTOP = 0.8;
const ZOOM_MIN_MOBILE = 0.01;
const ZOOM_MAX_MOBILE = 1;

// Multiplier applied to the computed "fit-to-viewport" initial zoom scale.
// (1 keeps current behavior; increase >1 to zoom in more initially on mobile/desktop.)
const INITIAL_ZOOM_MULTIPLIER_DESKTOP = 1.4;
const INITIAL_ZOOM_MULTIPLIER_MOBILE = 1.0;

// Cluster mode: when zoomed below the viewport-specific threshold, large groups collapse
// into a single organic "cloud" shape and smaller groups disappear entirely.
// This avoids per-node DOM/physics work when the user can't visually distinguish
// individual nodes anyway.
const ZOOM_CLUSTER_THRESHOLD_DESKTOP = 0.08;
const ZOOM_CLUSTER_THRESHOLD_MOBILE = 0.08;
const CLUSTER_GROUP_MIN_NODES = 8;
const CLUSTER_EXIT_HYSTERESIS = 0.02;
const CLUSTER_EXCLUDED_COLORS = new Set(['#9e9e9e', '#999999', '#808080', 'gray', 'grey']);

const MOBILE_BREAKPOINT_PX = 768;
function isMobileViewport() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
}

function isDesktopSafariBrowser() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isSafariEngine = /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\/|CriOS\/|FxiOS\//.test(ua);
  const isMacDesktop = /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) === 0;
  return isSafariEngine && isMacDesktop;
}

function getZoomClusterThreshold() {
  return isMobileViewport() ? ZOOM_CLUSTER_THRESHOLD_MOBILE : ZOOM_CLUSTER_THRESHOLD_DESKTOP;
}

function shouldExcludeClusterColor(color) {
  if (color == null) return true;
  return CLUSTER_EXCLUDED_COLORS.has(String(color).trim().toLowerCase());
}

const MIN_CLUSTER_COLOR_RADIUS = 14;
const MIN_CLUSTER_AREA = Math.PI * MIN_CLUSTER_COLOR_RADIUS * MIN_CLUSTER_COLOR_RADIUS;

function buildColorClusterCircles(groupNodes, getNodeColor, groupCenter) {
  const nodesByColor = new Map();
  groupNodes.forEach((n) => {
    const color = getNodeColor(n);
    if (shouldExcludeClusterColor(color)) return;
    if (!nodesByColor.has(color)) nodesByColor.set(color, []);
    nodesByColor.get(color).push(n);
  });

  const circles = [];
  nodesByColor.forEach((nodes, color) => {
    if (!nodes.length) return;

    let bestDx = 0;
    let bestDy = 0;
    let maxDistSq = 0;
    let midX = nodes[0].x;
    let midY = nodes[0].y;

    if (nodes.length > 1) {
      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const distSq = dx * dx + dy * dy;
          if (distSq > maxDistSq) {
            maxDistSq = distSq;
            bestDx = dx;
            bestDy = dy;
            midX = (nodes[i].x + nodes[j].x) / 2;
            midY = (nodes[i].y + nodes[j].y) / 2;
          }
        }
      }
    }

    const furthestDistance = Math.sqrt(bestDx * bestDx + bestDy * bestDy);
    const rawRadius = furthestDistance / 2;
    const radius = Math.max(MIN_CLUSTER_COLOR_RADIUS, rawRadius);
    const area = Math.max(MIN_CLUSTER_AREA, Math.PI * radius * radius);
    const density = nodes.length / area;

    circles.push({
      color,
      count: nodes.length,
      cx: midX - groupCenter.x,
      cy: midY - groupCenter.y,
      radius,
      density
    });
  });

  return circles;
}

// Canvas: circle clip, soft rim; white↔grey onset and drag clamp share CANVAS_WHITE_INSET / OUTER_RADIUS.
const LEGACY_SQUARE_SIDE = 25000;
const CANVAS_SCALE = 0.85;
const CIRCLE_DIAMETER = LEGACY_SQUARE_SIDE * 1.5 * CANVAS_SCALE;
const CIRCLE_RADIUS = CIRCLE_DIAMETER / 2;
const CIRCLE_CX = CIRCLE_RADIUS;
const CIRCLE_CY = CIRCLE_RADIUS;

const CANVAS_EDGE_FEATHER_HALF = 2800;
const CANVAS_BACKDROP_RADIUS = CIRCLE_RADIUS + CANVAS_EDGE_FEATHER_HALF;
const VISUAL_SCENE_EXTENT = CIRCLE_DIAMETER + 2 * CANVAS_EDGE_FEATHER_HALF;
const CANVAS_WHITE_INSET = 1300;
const CANVAS_WHITE_OUTER_RADIUS = Math.max(0, CIRCLE_RADIUS - CANVAS_EDGE_FEATHER_HALF - CANVAS_WHITE_INSET);

// clamping and color-map helpers are extracted in feature modules.

////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////

// 4. Component definition and state initialization

const NetworkGraph = ({ colorBy, setColorBy, data }) => {
  const svgRef = useRef();
  const controlsRef = useRef(null);
  const zoomRef = useRef(null);
  const zoomCleanupRef = useRef(null);
  // Refs that mirror colorBy / colorMaps so cluster tooltip handlers (bound
  // inside the data-keyed main effect) always read the latest values.
  const colorByRef = useRef(colorBy);
  const colorMapsRef = useRef({});
  const [colorMaps, setColorMaps] = useState({});
  const [darkSurface, setDarkSurface] = useState(false);

  useEffect(() => { colorByRef.current = colorBy; }, [colorBy]);
  useEffect(() => { colorMapsRef.current = colorMaps; }, [colorMaps]);

///////////////////////////////////////////////////////
///////////////////////////////////////////////////////

// 5. Color-map generation useEffect

  // Build one `colorMaps[key] = { value→color }` map for *all* keys in one pass
  useEffect(() => {
    const nodes = data.nodes;
    if (!nodes?.length) {
        setColorMaps({});
      return;
    }
    setColorMaps(buildColorMaps(nodes));
  }, [data]);


////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////

// 8. getNodeColor & createNodePath (node coloring and multi-value splitting)

  const getNodeColor = useCallback((d) => {
    return getNodeColorFromMaps(d, colorBy, colorMaps);
  }, [colorMaps, colorBy]);

  /**
   * Build slice information for ANY comma‑separated multivalue field.
   * Returns { items, colorMap } or null if single‑valued.
   */
  const createNodePath = useCallback((d) => {
    return createNodePathInfo(d, colorBy, colorMaps);
  }, [colorMaps, colorBy]);

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

// 9. setupZoom behavior configuration


  // Setup zoom behavior
  // Hybrid pan/zoom (Figma-ish on trackpad, Maps-ish on mouse):
  //   - trackpad pinch  -> zoom (wheel + ctrlKey/metaKey)
  //   - trackpad swipe  -> pan  (wheel, small/fractional/horizontal delta)
  //   - mouse wheel     -> zoom (focal at cursor)
  //   - shift + wheel   -> horizontal pan
  //   - left drag       -> pan  (handled by d3.zoom)
  //   - middle drag     -> pan  (custom pointer handler below)
  //   - dblclick        -> ignored
  const setupZoom = (svg, g, containerWidth, containerHeight, onTransformChange) => {
    const node = svg.node();

    const mobile = isMobileViewport();
    const ZOOM_MIN = mobile ? ZOOM_MIN_MOBILE : ZOOM_MIN_DESKTOP;
    const ZOOM_MAX = mobile ? ZOOM_MAX_MOBILE : ZOOM_MAX_DESKTOP;

    const zoom = d3.zoom()
      .scaleExtent([ZOOM_MIN, ZOOM_MAX])
      .translateExtent([
        [CIRCLE_CX - CANVAS_BACKDROP_RADIUS - 100, CIRCLE_CY - CANVAS_BACKDROP_RADIUS - 100],
        [CIRCLE_CX + CANVAS_BACKDROP_RADIUS + 100, CIRCLE_CY + CANVAS_BACKDROP_RADIUS + 100]
      ])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        onTransformChange?.(event.transform);
      })
      .filter((event) => {
        if (event.type === 'dblclick') return false;
        // Wheel is routed manually to support hybrid pan/zoom semantics.
        if (event.type === 'wheel') return false;
        // Allow left button (0) for normal pan; middle button (1) is handled separately.
        if (event.type === 'mousedown') return event.button === 0;
        return true;
      });

    // Fit the circular bounding box in the viewport
    const scaleX = containerWidth / VISUAL_SCENE_EXTENT;
    const scaleY = containerHeight / VISUAL_SCENE_EXTENT;
    const initialScaleBase = Math.min(scaleX, scaleY);
    const initialZoomMultiplier = mobile ? INITIAL_ZOOM_MULTIPLIER_MOBILE : INITIAL_ZOOM_MULTIPLIER_DESKTOP;
    const initialScale = initialScaleBase * initialZoomMultiplier;

    const initialTransform = d3.zoomIdentity
      .translate(containerWidth / 2, containerHeight / 2)
      .scale(initialScale)
      .translate(-CIRCLE_CX, -CIRCLE_CY);

    svg.call(zoom)
      .call(zoom.transform, initialTransform)
      .call(zoom.touchable(true));

    // Pan cursor: only force grabbing while actively panning the view (not on node drag).
    // Idle graph uses cursor: default from CSS; clear inline cursor when done so that applies again.
    const resetSvgPanCursor = () => {
      svg.style('cursor', null);
    };

    const clearGrabIfNoButtonHeld = (e) => {
      if (!e.buttons) resetSvgPanCursor();
    };

    const isViewPanCursorSurface = (event) => {
      const el = event.target;
      return Boolean(el && typeof el.closest === 'function' && !el.closest('.node'));
    };

    svg
      .on('mousedown.indicator', (event) => {
        if (event.button !== 0) return;
        if (!isViewPanCursorSurface(event)) return;
        svg.style('cursor', 'grabbing');
      })
      .on('mouseup.indicator', (event) => {
        if (event.button !== 0) return;
        resetSvgPanCursor();
      });

    window.addEventListener('pointerup', resetSvgPanCursor, true);
    window.addEventListener('pointercancel', resetSvgPanCursor, true);
    window.addEventListener('pointermove', clearGrabIfNoButtonHeld, true);

    const onWheel = (e) => {
      e.preventDefault();
      const sel = d3.select(node);
      const isPinchZoom = e.ctrlKey || e.metaKey;

      if (isPinchZoom) {
        const [px, py] = d3.pointer(e, node);
        const sensitivity = 0.01;
        const factor = Math.exp(-e.deltaY * sensitivity);
        sel.call(zoom.scaleBy, factor, [px, py]);
        return;
      }

      // pan: convert screen-pixel delta to world units (divide by current scale k).
      const t = d3.zoomTransform(node);
      const dx = e.shiftKey ? -e.deltaY : -e.deltaX;
      const dy = e.shiftKey ? 0 : -e.deltaY;
      sel.call(zoom.translateBy, dx / t.k, dy / t.k);
    };
    node.addEventListener('wheel', onWheel, { passive: false });

    // Middle-mouse drag pan (kept separate from d3.zoom which only handles button 0).
    const onPointerDown = (e) => {
      if (e.button !== 1) return;
      e.preventDefault();
      try { node.setPointerCapture(e.pointerId); } catch (_) { /* not all targets support capture */ }

      let lastX = e.clientX;
      let lastY = e.clientY;
      svg.style('cursor', 'grabbing');

      const onMove = (m) => {
        const dx = m.clientX - lastX;
        const dy = m.clientY - lastY;
        lastX = m.clientX;
        lastY = m.clientY;
        const t = d3.zoomTransform(node);
        d3.select(node).call(zoom.translateBy, dx / t.k, dy / t.k);
      };
      const onUp = () => {
        node.removeEventListener('pointermove', onMove);
        node.removeEventListener('pointerup', onUp);
        node.removeEventListener('pointercancel', onUp);
        svg.style('cursor', null);
      };
      node.addEventListener('pointermove', onMove);
      node.addEventListener('pointerup', onUp);
      node.addEventListener('pointercancel', onUp);
    };
    node.addEventListener('pointerdown', onPointerDown);

    // Suppress browser auto-scroll cursor / context menu on middle button.
    const onAuxClick = (e) => {
      if (e.button === 1) e.preventDefault();
    };
    node.addEventListener('auxclick', onAuxClick);

    const cleanup = () => {
      window.removeEventListener('pointerup', resetSvgPanCursor, true);
      window.removeEventListener('pointercancel', resetSvgPanCursor, true);
      window.removeEventListener('pointermove', clearGrabIfNoButtonHeld, true);
      svg.on('mousedown.indicator', null).on('mouseup.indicator', null);
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('pointerdown', onPointerDown);
      node.removeEventListener('auxclick', onAuxClick);
    };

    return { zoom, cleanup };
  };


  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current?.parentElement) {
        const svg = d3.select(svgRef.current);
        svg.attr('viewBox', `0 0 ${svgRef.current.parentElement.clientWidth} ${window.innerHeight * 0.7}`);

        if (zoomRef.current) {
          svg.select('g').attr('transform', d3.zoomTransform(svg.node()));
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Touch event handler
  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return undefined;

    const handleTouch = (e) => {
      if (e.touches?.length >= 2) e.preventDefault();
    };

    const options = { passive: false };
    svgElement.addEventListener('touchmove', handleTouch, options);
    svgElement.addEventListener('touchstart', handleTouch, options);

    return () => {
      svgElement.removeEventListener('touchmove', handleTouch);
      svgElement.removeEventListener('touchstart', handleTouch);
    };
  }, []);

  // Main visualization effect
  useEffect(() => {
    if (!svgRef.current || !data || !data.nodes || data.nodes.length === 0) return undefined;
    const isMobile = isMobileViewport();
    const isDesktopSafari = isDesktopSafariBrowser();
    const enableHeavySvgEffects = !isMobile && !isDesktopSafari;

    if (zoomCleanupRef.current) {
      zoomCleanupRef.current();
      zoomCleanupRef.current = null;
    }
    // Declared outside `try` so effect cleanup can remove listeners / stop simulators safely.
    let handleGlobalDragRelease = null;
    let simulation = null;
    let groupMiniSimInstance = null;
    /** Assigned inside try once helpers exist; cleanup always calls a safe no-op if render failed. */
    let teardownGroupMiniSimOnly = () => {};
    try {
      const containerWidth = svgRef.current.parentElement.clientWidth || 800;
      const containerHeight = window.innerHeight * 0.7 || 600;

      const width = containerWidth;
      const height = containerHeight;

      // Clear previous SVG content
      d3.select(svgRef.current).selectAll('*').remove();

      // Create SVG with responsive sizing
      const svg = d3.select(svgRef.current)
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMidYMid meet');

      const defs = svg.append('defs');
      defs.append('clipPath')
        .attr('id', 'viewport-circle-clip')
        .append('circle')
        .attr('cx', CIRCLE_CX)
        .attr('cy', CIRCLE_CY)
        .attr('r', CIRCLE_RADIUS);

      const R_grad = CANVAS_BACKDROP_RADIUS;
      const H = CANVAS_EDGE_FEATHER_HALF;
      const pctOfDist = dist => `${(Math.min(dist, R_grad) / R_grad) * 100}%`;

      const canvasEdgeGrad = defs.append('radialGradient')
        .attr('id', 'canvas-edge-soft')
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('cx', CIRCLE_CX)
        .attr('cy', CIRCLE_CY)
        .attr('fx', CIRCLE_CX)
        .attr('fy', CIRCLE_CY)
        .attr('r', R_grad);

      // Smoother black→white backdrop ramp (avoid visible “banding” / seam).
      // We generate many stops between a slightly earlier transition start and the
      // outer radius, using easing + a dense color interpolation.
      const WHITE_EDGE = '#fafbfc';
      const transitionStartR = Math.max(0, CANVAS_WHITE_OUTER_RADIUS - H * 0.22);
      const fadeStartU = 0.94; // start fading to transparent only near the very edge
      const fadePow = 0.85; // lower = gentler fade curve
      const stopCount = 40; // denser stops => fewer chances of visible banding

      const colorInterp = d3.interpolateRgbBasis([
        '#fafbfc',
        '#eef0f4',
        '#d4d7de',
        '#a8adb8',
        '#8b909b',
        '#6e737d',
        '#4b4e56',
        '#32343a',
        '#1b1c20',
        '#0a0a0b',
        '#000000',
      ]);

      // Keep the early region flat/white so the inner disk feels crisp.
      canvasEdgeGrad.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', WHITE_EDGE)
        .attr('stop-opacity', 1);

      const smoothstep = (t) => t * t * (3 - 2 * t);

      for (let i = 0; i < stopCount; i++) {
        const u = i / (stopCount - 1); // 0..1 across (transitionStartR..R_grad)
        const r = transitionStartR + (R_grad - transitionStartR) * u;
        const offset = pctOfDist(r);
        const eased = smoothstep(u);

        // Fade only in the last ~6% of the radius; this preserves the “solid”
        // look before blending into the black page background.
        const opacity = u < fadeStartU
          ? 1
          : Math.pow((1 - u) / (1 - fadeStartU), fadePow);

        canvasEdgeGrad.append('stop')
          .attr('offset', offset)
          .attr('stop-color', colorInterp(eased))
          .attr('stop-opacity', opacity);
      }

      if (enableHeavySvgEffects) {
        // Keep blur polish on desktop only; skip on mobile for GPU headroom.
        const backdropSoften = defs.append('filter')
          .attr('id', 'backdrop-soften')
          .attr('x', '-15%')
          .attr('y', '-15%')
          .attr('width', '130%')
          .attr('height', '130%');
        backdropSoften.append('feGaussianBlur')
          .attr('in', 'SourceGraphic')
          .attr('stdDeviation', 2.2);
      }
      defs.append('marker')
        .attr('id', 'arrow')
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 1)
        .attr('refY', 0)
        .attr('markerWidth', 30)
        .attr('markerHeight', 30)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', '#808080');

      if (enableHeavySvgEffects) {
        // Keep cluster cloud blur on desktop only; skip on mobile.
        const clusterFilter = defs.append('filter')
          .attr('id', 'cluster-cloud')
          .attr('x', '-30%')
          .attr('y', '-30%')
          .attr('width', '160%')
          .attr('height', '160%');
        clusterFilter.append('feGaussianBlur')
          .attr('in', 'SourceGraphic')
          .attr('stdDeviation', 14)
          .attr('result', 'blur');
        clusterFilter.append('feColorMatrix')
          .attr('in', 'blur')
          .attr('mode', 'matrix')
          .attr('values', '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 14 -6');
      }

      // Create a group for the visualization (zoom target)
      const g = svg.append('g');

      g.append('g')
        .attr('class', 'canvas-backdrop')
        .attr('pointer-events', 'none')
        .append('circle')
        .attr('cx', CIRCLE_CX)
        .attr('cy', CIRCLE_CY)
        .attr('r', CANVAS_BACKDROP_RADIUS)
        .attr('fill', 'url(#canvas-edge-soft)')
        .attr('filter', enableHeavySvgEffects ? 'url(#backdrop-soften)' : null);

      const world = g.append('g')
        .attr('clip-path', 'url(#viewport-circle-clip)')
        .attr('class', 'network-world');

      /* ──────────  GROUP‑AWARE LAYOUT (inside disk) ────────── */
      const groupMap = buildGroupsFromData(data.nodes, data.links);
      const groupCount = Math.max(...groupMap.values()) + 1;

      const groupSizes = Array.from({ length: groupCount }, () => 0);
      data.nodes.forEach(n => { groupSizes[groupMap.get(n.id)] += 1; });

      const BASE_R = 200;
      const PX_PER_NODE = 25;
      const groupR = groupSizes.map(s => BASE_R + PX_PER_NODE * Math.sqrt(s));

      const PAD = 700;
      const centres = Array.from({ length: groupCount }, () => null);
      const rng = () => Math.random();

      const uniformPointInDisk = (maxDistFromCentre) => {
        const theta = rng() * 2 * Math.PI;
        const radius = Math.sqrt(rng()) * Math.max(0, maxDistFromCentre);
        return {
          x: CIRCLE_CX + radius * Math.cos(theta),
          y: CIRCLE_CY + radius * Math.sin(theta)
        };
      };

      const movableLimit = Math.max(0, CANVAS_WHITE_OUTER_RADIUS - NODE_RADIUS - 10);
      const clampToMovableDisk = (x, y) => {
        const dx = x - CIRCLE_CX;
        const dy = y - CIRCLE_CY;
        const dist = Math.hypot(dx, dy);
        if (!Number.isFinite(dist) || dist <= movableLimit || dist === 0) return { x, y };
        const s = movableLimit / dist;
        return { x: CIRCLE_CX + dx * s, y: CIRCLE_CY + dy * s };
      };

      // Place larger groups first and choose the best candidate (maximizes
      // clearance to both existing groups and the outer wall) to avoid clumping.
      const groupOrder = Array.from({ length: groupCount }, (_, gi) => gi)
        .sort((a, b) => groupR[b] - groupR[a]);
      const candidateCount = 220;
      const golden = Math.PI * (3 - Math.sqrt(5));
      groupOrder.forEach((gi, orderIndex) => {
        const maxCenterDist = Math.max(140, movableLimit - groupR[gi] - PAD);
        let best = null;
        let bestScore = -Infinity;
        const nCandidates = Math.max(36, candidateCount - orderIndex * 5);

        for (let i = 0; i < nCandidates; i++) {
          const u = (i + 0.5) / nCandidates;
          const theta = i * golden + rng() * 0.18;
          const radius = Math.sqrt(u) * maxCenterDist;
          const cand = {
            x: CIRCLE_CX + radius * Math.cos(theta),
            y: CIRCLE_CY + radius * Math.sin(theta)
          };

          const wallClearance = maxCenterDist - radius;
          let overlapPenalty = 0;
          let nearestGap = Infinity;
          for (let j = 0; j < groupCount; j++) {
            const c = centres[j];
            if (!c) continue;
            const dx = c.x - cand.x;
            const dy = c.y - cand.y;
            const dist = Math.hypot(dx, dy);
            const minGap = groupR[j] + groupR[gi] + PAD;
            const gap = dist - minGap;
            nearestGap = Math.min(nearestGap, gap);
            if (gap < 0) overlapPenalty += (-gap) * 1000;
          }
          if (!Number.isFinite(nearestGap)) nearestGap = maxCenterDist;

          const score = nearestGap * 2.5 + wallClearance - overlapPenalty;
          if (score > bestScore) {
            bestScore = score;
            best = cand;
          }
        }

        centres[gi] = best || uniformPointInDisk(maxCenterDist);
      });

      const nodesByGroupSeed = Array.from({ length: groupCount }, () => []);
      data.nodes.forEach((n) => {
        nodesByGroupSeed[groupMap.get(n.id)].push(n);
      });

      // Seed nodes with Poisson-like rejection inside each group's disk so they
      // start dispersed instead of piled near the center.
      for (let gi = 0; gi < groupCount; gi++) {
        const c = centres[gi] || { x: CIRCLE_CX, y: CIRCLE_CY };
        const groupNodes = nodesByGroupSeed[gi];
        const seeded = [];
        const radialLimit = Math.max(groupR[gi] - 36, 28);
        const minSepBase = Math.min(92, Math.max(42, NODE_RADIUS * 1.7));

        groupNodes.forEach((n, idx) => {
          let placed = null;
          for (let attempt = 0; attempt < 60; attempt++) {
            const θ = rng() * 2 * Math.PI;
            const r = Math.sqrt(rng()) * radialLimit;
            const cand = {
              x: c.x + r * Math.cos(θ),
              y: c.y + r * Math.sin(θ)
            };
            const clamped = clampToMovableDisk(cand.x, cand.y);
            const minSep = Math.max(20, minSepBase - attempt * 0.85);
            const isFarEnough = seeded.every((p) => {
              const dx = p.x - clamped.x;
              const dy = p.y - clamped.y;
              return (dx * dx + dy * dy) >= minSep * minSep;
            });
            if (isFarEnough) {
              placed = clamped;
              break;
            }
          }

          if (!placed) {
            const fallbackTheta = (idx + 1) * golden;
            const fallbackR = Math.sqrt((idx + 1) / (groupNodes.length + 1)) * radialLimit;
            placed = clampToMovableDisk(
              c.x + fallbackR * Math.cos(fallbackTheta),
              c.y + fallbackR * Math.sin(fallbackTheta)
            );
          }

          n.x = placed.x;
          n.y = placed.y;
          seeded.push(placed);
        });
      }

      let currentTransform = d3.zoomIdentity;
      let visibleGroups = new Set();
      let lastUiIsDark = null;
      let inClusterMode = false;

      const linkForce = d3.forceLink(data.links)
        .id(d => d.id)
        .distance(400)
        .strength(1);

      simulation = d3.forceSimulation(data.nodes)
        .force('link', linkForce)
        .force('collision', d3.forceCollide().radius(120))
        .alphaDecay(0.1) // controls cooldown speed
        .on('end', () => {
          simulation.stop(); // freeze after global layout settles
        });

      data.nodes.forEach((n) => {
        n.__groupIndex = groupMap.get(n.id);
      });
      data.links.forEach((l) => {
        l.__groupIndex = groupMap.get(l.source.id ?? l.source);
      });

      // Active + parked layers for DOM culling. Culled elements are moved to parked
      // layers (detached from visible world) and reattached when needed.
      const activeLinkLayer = world.append('g').attr('class', 'link-layer-active');
      const activeNodeLayer = world.append('g').attr('class', 'node-layer-active');
      const activeClusterLayer = world.append('g').attr('class', 'cluster-layer-active');
      const parkedRoot = g.append('g')
        .attr('class', 'parked-dom-root')
        .attr('display', 'none')
        .attr('pointer-events', 'none');
      const parkedLinkLayer = parkedRoot.append('g').attr('class', 'link-layer-parked');
      const parkedNodeLayer = parkedRoot.append('g').attr('class', 'node-layer-parked');
      const parkedClusterLayer = parkedRoot.append('g').attr('class', 'cluster-layer-parked');

      // Create links
      const fullLinks = activeLinkLayer.selectAll('.link-full')
        .data(data.links)
        .enter()
        .append('path')
        .attr('class', 'link-full')
        .attr('fill', 'none')
        .attr('stroke', '#999')
        .attr('stroke-width', 3);
      const arrowLinks = activeLinkLayer.selectAll('.link-arrow')
        .data(data.links)
        .enter()
        .append('path')
        .attr('class', 'link-arrow')
        .attr('fill', 'none')
        .attr('stroke', 'none')
        .attr('marker-end', 'url(#arrow)');

      // Create nodes
      const node = activeNodeLayer
        .selectAll('.node')
        .data(data.nodes)
        .enter()
        .append('g')
        .attr('class', 'node')
        .call(d3.drag()
          .on('start', dragstarted)
          .on('drag', dragged)
          .on('end', dragended));

      // ── Cluster layer (zoom-out cloud blobs) ─────────────────────────────
      // One <g class="cluster"> per qualifying group. They start parked, then
      // get attached into activeClusterLayer in cluster mode.
      const clusterLayer = parkedClusterLayer;

      const computeGroupCentroid = (gi) => {
        let sx = 0;
        let sy = 0;
        let count = 0;
        for (const n of data.nodes) {
          if (n.__groupIndex === gi) {
            sx += n.x;
            sy += n.y;
            count += 1;
          }
        }
        if (count === 0) return { x: CIRCLE_CX, y: CIRCLE_CY };
        return { x: sx / count, y: sy / count };
      };

      const computeGroupClusterCircles = (gi) => {
        const groupNodes = [];
        for (const n of data.nodes) {
          if (n.__groupIndex === gi) groupNodes.push(n);
        }
        if (!groupNodes.length) return [];
        const center = computeGroupCentroid(gi);
        return buildColorClusterCircles(groupNodes, getNodeColor, center);
      };

      // Build one cluster <g> per qualifying group (hidden by default).
      const clusterGroupRecords = [];
      for (let gi = 0; gi < groupCount; gi++) {
        if (groupSizes[gi] < CLUSTER_GROUP_MIN_NODES) continue;
        const cg = clusterLayer.append('g')
          .attr('class', 'cluster')
          .attr('data-gi', gi)
          .attr('display', 'none')
          .style('cursor', 'pointer')
          .style('filter', enableHeavySvgEffects ? 'url(#cluster-cloud)' : null);
        clusterGroupRecords.push({ gi, sel: cg, size: groupSizes[gi] });
      }

      // Initial paint using current colorBy.
      clusterGroupRecords.forEach(({ gi, sel }) => {
        renderClusterContentsFromModule(sel, computeGroupClusterCircles(gi));
      });

      const fullLinksByGroup = Array.from({ length: groupCount }, () => []);
      fullLinks.each(function (d) {
        fullLinksByGroup[d.__groupIndex].push(this);
      });
      const arrowLinksByGroup = Array.from({ length: groupCount }, () => []);
      arrowLinks.each(function (d) {
        arrowLinksByGroup[d.__groupIndex].push(this);
      });
      const nodesByGroup = Array.from({ length: groupCount }, () => []);
      node.each(function (d) {
        nodesByGroup[d.__groupIndex].push(this);
      });

      let currentHighlight = null;

      /** Apply highlight/dim only to viewport-visible groups (same culling as physics/DOM). */
      const applyViewportHighlightClasses = () => {
        if (inClusterMode || groupCount === 0) return;

        for (let gi = 0; gi < groupCount; gi++) {
          if (!visibleGroups.has(gi)) continue;

          const ns = nodesByGroup[gi];
          for (let i = 0; i < ns.length; i++) {
            const d = d3.select(ns[i]).datum();
            const gId = groupMap.get(d.id);
            const isHi = currentHighlight !== null && gId === currentHighlight;
            d3.select(ns[i])
              .classed('highlight', isHi)
              .classed('dim', currentHighlight !== null && !isHi);
          }

          const fl = fullLinksByGroup[gi];
          for (let i = 0; i < fl.length; i++) {
            const lk = d3.select(fl[i]).datum();
            const s = groupMap.get(lk.source.id ?? lk.source);
            const t = groupMap.get(lk.target.id ?? lk.target);
            const isLinkHi = s === currentHighlight && t === currentHighlight;
            d3.select(fl[i])
              .classed('highlight', isLinkHi)
              .classed('dim', currentHighlight !== null && !isLinkHi);
          }

          const als = arrowLinksByGroup[gi];
          for (let i = 0; i < als.length; i++) {
            const lk = d3.select(als[i]).datum();
            const s = groupMap.get(lk.source.id ?? lk.source);
            const t = groupMap.get(lk.target.id ?? lk.target);
            const isLinkHi = s === currentHighlight && t === currentHighlight;
            d3.select(als[i])
              .classed('highlight', isLinkHi)
              .classed('dim', currentHighlight !== null && !isLinkHi);
          }
        }
      };

      // Per-group mini simulation while dragging a node (hold); clicks only toggle highlight/focus.
      // DOM updates use pre-bucketed elements only — never selectAll + filter each tick.

      teardownGroupMiniSimOnly = () => {
        if (groupMiniSimInstance) {
          groupMiniSimInstance.stop();
          groupMiniSimInstance = null;
        }
      };

      const resumeMainSimulationAfterGroupSim = () => {
        simulation.alphaTarget(0);
        simulation.alpha(0.08).restart();
      };

      const stopGroupMiniSimFully = () => {
        teardownGroupMiniSimOnly();
        resumeMainSimulationAfterGroupSim();
      };

      const updateGroupMiniDom = (gi) => {
        const ns = nodesByGroup[gi];
        for (let i = 0; i < ns.length; i += 1) {
          const el = ns[i];
          const nd = d3.select(el).datum();
          d3.select(el).attr('transform', `translate(${nd.x},${nd.y})`);
        }
        const fl = fullLinksByGroup[gi];
        for (let i = 0; i < fl.length; i += 1) {
          const el = fl[i];
          const lk = d3.select(el).datum();
          d3.select(el).attr('d', computeLinkPath(lk));
        }
        const als = arrowLinksByGroup[gi];
        for (let i = 0; i < als.length; i += 1) {
          const el = als[i];
          const lk = d3.select(el).datum();
          d3.select(el).attr('d', arrowPathAtFraction(lk, 1 / 3));
        }
      };

      const startGroupMiniSim = (gi) => {
        if (inClusterMode) return false;
        if (!Number.isFinite(gi)) return false;
        teardownGroupMiniSimOnly();
        simulation.stop();

        const groupNodes = data.nodes.filter((n) => n.__groupIndex === gi);
        const groupLinks = data.links.filter((l) => {
          const s = groupMap.get(l.source.id ?? l.source);
          const t = groupMap.get(l.target.id ?? l.target);
          return s === gi && t === gi;
        });
        if (groupNodes.length === 0) {
          resumeMainSimulationAfterGroupSim();
          return false;
        }

        groupMiniSimInstance = d3
          .forceSimulation(groupNodes)
          .force('link', d3.forceLink(groupLinks).id((n) => n.id).distance(300).strength(1))
          .force('collision', d3.forceCollide().radius(80))
          .alphaDecay(0)
          .force('charge', d3.forceManyBody().strength(-1500))
          .on('tick', () => {
            clampNodesToDisk(groupNodes);
            updateGroupMiniDom(gi);
          });

        groupMiniSimInstance.alpha(1).restart();
        return true;
      };

      const clusterByGroup = Array.from({ length: groupCount }, () => null);
      clusterGroupRecords.forEach(({ gi, sel }) => {
        clusterByGroup[gi] = sel.node();
      });

      let nodeAttached = Array.from({ length: groupCount }, () => true);
      let linkAttached = Array.from({ length: groupCount }, () => true);
      let clusterAttached = Array.from({ length: groupCount }, () => false);

      const moveElems = (elems, parentNode) => {
        if (!parentNode || !elems?.length) return;
        elems.forEach((el) => {
          if (el && el.parentNode !== parentNode) {
            parentNode.appendChild(el);
          }
        });
      };

      const setGroupNodeAttachment = (gi, attach) => {
        if (nodeAttached[gi] === attach) return;
        moveElems(nodesByGroup[gi], attach ? activeNodeLayer.node() : parkedNodeLayer.node());
        nodeAttached[gi] = attach;
      };

      const setGroupLinkAttachment = (gi, attach) => {
        if (linkAttached[gi] === attach) return;
        const target = attach ? activeLinkLayer.node() : parkedLinkLayer.node();
        moveElems(fullLinksByGroup[gi], target);
        moveElems(arrowLinksByGroup[gi], target);
        linkAttached[gi] = attach;
      };

      const setGroupClusterAttachment = (gi, attach) => {
        if (clusterAttached[gi] === attach) return;
        const el = clusterByGroup[gi];
        if (!el) {
          clusterAttached[gi] = false;
          return;
        }
        const target = attach ? activeClusterLayer.node() : parkedClusterLayer.node();
        if (el.parentNode !== target) target.appendChild(el);
        clusterAttached[gi] = attach;
      };

      const updateUiSurfaceTheme = () => {
        const controlsEl = controlsRef.current;
        if (!controlsEl) return;

        const graphRect = svgRef.current.getBoundingClientRect();
        const panelRect = controlsEl.getBoundingClientRect();
        if (!graphRect.width || !graphRect.height) return;

        // Panel center in graph-local screen space.
        const sx = panelRect.left + panelRect.width / 2 - graphRect.left;
        const sy = panelRect.top + panelRect.height / 2 - graphRect.top;

        // Convert from screen -> world using the current zoom transform.
        const wx = (sx - currentTransform.x) / currentTransform.k;
        const wy = (sy - currentTransform.y) / currentTransform.k;
        const dist = Math.hypot(wx - CIRCLE_CX, wy - CIRCLE_CY);

        // Outside/near outer ring is visually dark; inner disk is bright.
        const uiIsDark = dist >= CANVAS_WHITE_OUTER_RADIUS * 1.02;
        if (uiIsDark !== lastUiIsDark) {
          lastUiIsDark = uiIsDark;
          setDarkSurface(uiIsDark);
        }
      };

      const enterClusterMode = () => {
        inClusterMode = true;

        // DOM-cull all individual nodes/links while clustered.
        for (let gi = 0; gi < groupCount; gi++) {
          setGroupNodeAttachment(gi, false);
          setGroupLinkAttachment(gi, false);
        }

        // Freeze physics for ALL nodes so they hold position while clustered.
        data.nodes.forEach((n) => {
          if (!n.__clusterFrozen) {
            n.__prevFx = n.fx;
            n.__prevFy = n.fy;
            n.fx = n.x;
            n.fy = n.y;
            n.__clusterFrozen = true;
          }
        });
        simulation.stop();

        // Attach and position only qualifying clusters.
        clusterGroupRecords.forEach(({ gi, sel, size }) => {
          const c = computeGroupCentroid(gi);
          if (size >= CLUSTER_GROUP_MIN_NODES) {
            setGroupClusterAttachment(gi, true);
          }
          sel.attr('transform', `translate(${c.x},${c.y})`)
            .attr('display', null)
            .classed('culled', false);
        });
        for (let gi = 0; gi < groupCount; gi++) {
          if (groupSizes[gi] < CLUSTER_GROUP_MIN_NODES) {
            setGroupClusterAttachment(gi, false);
          }
        }

        visibleGroups = new Set();
      };

      const exitClusterMode = () => {
        inClusterMode = false;

        // Park all clusters when leaving cluster mode.
        clusterGroupRecords.forEach(({ gi, sel }) => {
          sel.attr('display', null).classed('culled', false);
          setGroupClusterAttachment(gi, false);
        });

        // Restore prior fx/fy on cluster-frozen nodes (which may have been null,
        // or pinned by the older viewport-cull path).
        data.nodes.forEach((n) => {
          if (n.__clusterFrozen) {
            n.fx = n.__prevFx ?? null;
            n.fy = n.__prevFy ?? null;
            n.__prevFx = undefined;
            n.__prevFy = undefined;
            n.__clusterFrozen = false;
            // Clear the older viewport-cull flag too, since we just set fx/fy.
            n.__culledFixed = false;
          }
        });

        // Force the cull below to re-evaluate from a clean slate.
        visibleGroups = new Set();
        simulation.alpha(0.2).restart();
      };

      const isClusterWanted = () => {
        const clusterThreshold = getZoomClusterThreshold();
        if (inClusterMode) {
          return currentTransform.k < (clusterThreshold + CLUSTER_EXIT_HYSTERESIS);
        }
        return currentTransform.k < clusterThreshold;
      };

      const commitClusterModeIfNeeded = () => {
        const wantClusterMode = isClusterWanted();
        if (wantClusterMode === inClusterMode) return false;
        if (wantClusterMode) {
          enterClusterMode();
        } else {
          exitClusterMode();
        }
        return true;
      };

      const applyGroupCulling = () => {
        const t = currentTransform;
        const wantClusterMode = isClusterWanted();
        if (wantClusterMode !== inClusterMode) {
          commitClusterModeIfNeeded();
        }
        if (inClusterMode) return;

        // ── Normal mode: viewport cull ──
        const margin = NODE_RADIUS + 20;
        const minX = (-t.x) / t.k - margin;
        const maxX = (width - t.x) / t.k + margin;
        const minY = (-t.y) / t.k - margin;
        const maxY = (height - t.y) / t.k + margin;

        const seen = Array.from({ length: groupCount }, () => false);
        let seenCount = 0;
        for (const n of data.nodes) {
          const gi = n.__groupIndex;
          if (seen[gi]) continue;
          if (n.x >= minX && n.x <= maxX && n.y >= minY && n.y <= maxY) {
            seen[gi] = true;
            seenCount += 1;
            if (seenCount === groupCount) break;
          }
        }

        const nextVisibleGroups = new Set();
        seen.forEach((v, i) => { if (v) nextVisibleGroups.add(i); });
        if (nextVisibleGroups.size === 0 && groupCount > 0) {
          nextVisibleGroups.add(0);
        }

        const changed =
          nextVisibleGroups.size !== visibleGroups.size
          || [...nextVisibleGroups].some(gi => !visibleGroups.has(gi));
        visibleGroups = nextVisibleGroups;
        if (!changed) return;

        // DOM-cull by moving full groups between active and parked layers.
        for (let gi = 0; gi < groupCount; gi++) {
          const shouldAttach = visibleGroups.has(gi);
          setGroupNodeAttachment(gi, shouldAttach);
          setGroupLinkAttachment(gi, shouldAttach);
          setGroupClusterAttachment(gi, false);
        }

        // Freeze physics for offscreen groups and unfreeze when visible again.
        data.nodes.forEach((n) => {
          const isVisible = visibleGroups.has(n.__groupIndex);
          if (!isVisible) {
            if (!n.__culledFixed && n.fx == null && n.fy == null) {
              n.fx = n.x;
              n.fy = n.y;
              n.__culledFixed = true;
            }
          } else if (n.__culledFixed) {
            n.fx = null;
            n.fy = null;
            n.__culledFixed = false;
          }
        });

        simulation.alpha(0.12).restart();
        updateUiSurfaceTheme();
        applyViewportHighlightClasses();
      };

      const { zoom, cleanup: cleanupZoom } = setupZoom(
        svg,
        g,
        width,
        height,
        (transform) => {
          currentTransform = transform;
          applyGroupCulling();
          updateUiSurfaceTheme();
        }
      );
      zoomRef.current = zoom;
      zoomCleanupRef.current = cleanupZoom;

      let suppressNextClick = false;

      node.on('click', (event, d) => {
        if (suppressNextClick) {
          suppressNextClick = false;
          return;
        }
        const grp = groupMap.get(d.id);
        // toggle on/off
        currentHighlight = (currentHighlight === grp ? null : grp);

        // Highlight/dim only nodes & links in viewport-visible groups (see applyGroupCulling).
        applyViewportHighlightClasses();

        // Click only toggles focus/highlight; in-group expansion runs while dragging (hold).
        if (currentHighlight == null) {
          stopGroupMiniSimFully();
        }
      });

      // Create tooltip
      d3.select('body').selectAll('.tooltip').remove();
      const tooltip = d3.select('body').append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('pointer-events', 'none');

      // ── Cluster interactivity (hover tooltip + click-to-zoom) ────────────
      // Tooltip shows the colorBy-value breakdown for that group; click
      // animates the zoom past ZOOM_CLUSTER_THRESHOLD so the cluster
      // expands back into individual nodes. Uses colorByRef / colorMapsRef
      // so it stays correct after the user changes the colorBy selection
      // (the main effect only re-runs on `data` changes, not colorBy).
      const computeGroupValueCounts = (gi, key) => {
        const counts = new Map();
        for (const n of data.nodes) {
          if (n.__groupIndex !== gi) continue;
          const field = n[key];
          const val = field == null || field === ''
            ? '(unknown)'
            : String(field).split(',')[0].trim();
          counts.set(val, (counts.get(val) || 0) + 1);
        }
        return counts;
      };

      clusterGroupRecords.forEach(({ gi, sel, size }) => {
        const clusterSel = sel.style('pointer-events', 'auto');
        if (!isMobile) {
          clusterSel
            .on('mouseover', (event) => {
              const liveColorBy = colorByRef.current;
              const liveColorMaps = colorMapsRef.current || {};
              const valCounts = computeGroupValueCounts(gi, liveColorBy);
              const colorMap = liveColorMaps[liveColorBy] || {};
              const top = [...valCounts.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, 6);

              let html = `<h4 style="margin:0 0 4px 0;">Group #${gi} \u00b7 ${size} nodes</h4>`;
              html += `<div style="font-size:0.8em;opacity:0.7;margin-bottom:6px;">By: ${liveColorBy}</div>`;
              html += '<div style="display:flex;flex-direction:column;gap:3px;">';
              top.forEach(([val, count]) => {
                const color = colorMap[val] || '#9e9e9e';
                const pct = ((count / size) * 100).toFixed(0);
                html += `<div style="display:flex;align-items:center;gap:6px;font-size:0.85em;">`
                  + `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};flex:none;"></span>`
                  + `<span>${val}: ${count} (${pct}%)</span>`
                  + `</div>`;
              });
              if (valCounts.size > top.length) {
                html += `<div style="opacity:0.6;font-size:0.8em;margin-top:2px;">+${valCounts.size - top.length} more\u2026</div>`;
              }
              html += '</div>';
              html += '<div style="opacity:0.6;font-size:0.75em;margin-top:6px;">Click to zoom in</div>';

              tooltip
                .html(html)
                .style('left', (event.pageX + 12) + 'px')
                .style('top', (event.pageY + 12) + 'px')
                .classed('visible', true)
                .transition()
                .duration(200)
                .style('opacity', 0.95)
                .style('transform', 'translateY(0)');
            })
            .on('mousemove', (event) => {
              tooltip
                .style('left', (event.pageX + 12) + 'px')
                .style('top', (event.pageY + 12) + 'px');
            })
            .on('mouseout', () => {
              tooltip.transition()
                .duration(200)
                .style('opacity', 0)
                .style('transform', 'translateY(10px)')
                .on('end', function () {
                  tooltip.classed('visible', false);
                });
            });
        }
        clusterSel.on('click', () => {
          const c = computeGroupCentroid(gi);
          const targetK = getZoomClusterThreshold() * 2.5;
          const target = d3.zoomIdentity
            .translate(width / 2, height / 2)
            .scale(targetK)
            .translate(-c.x, -c.y);
          svg.transition()
            .duration(500)
            .call(zoom.transform, target);
          tooltip.transition().duration(150).style('opacity', 0)
            .on('end', function () { tooltip.classed('visible', false); });
        });
      });

      // Add node shapes and tooltips
      node.each(function (d) {
        const nodeGroup = d3.select(this);
        const nodePathInfo = createNodePath(d);

        if (!isMobile) {
          nodeGroup
            .on('mouseover', (event) => {
              const major = [d.cu_major, d.cu_major_1, d.major]
                .map((value) => (value == null ? '' : String(value).trim()))
                .find((value) => value !== '') || 'N/A';
              let html = `<h4>ID: ${d.id}</h4>`;
              html += `<p><strong>Major:</strong> ${major}</p>`;

              tooltip
                .html(html)
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 28) + 'px')
                .classed('visible', true)
                .transition()
                .duration(200)
                .style('opacity', 0.9)
                .style('transform', 'translateY(0)');
            })
            .on('mouseout', () => {
              tooltip.transition()
                .duration(300)
                .style('opacity', 0)
                .style('transform', 'translateY(10px)')
                .on('end', function () {
                  tooltip.classed('visible', false);
                });
            });
        }

        renderNodeVisual(nodeGroup, d, nodePathInfo, {
          colorMaps,
          colorBy,
          getNodeColor,
          simplified: false
        });
      });

      // Keep node centers inside the draggable inner disk
      simulation.on('tick', () => {
        // In cluster mode, individual nodes/links are hidden and physics is
        // frozen, so skip all per-node DOM writes for performance.
        if (inClusterMode) {
          return;
        }

        clampNodesToDisk(data.nodes);

        fullLinks
          .filter(d => visibleGroups.has(d.__groupIndex))
          .attr('d', d => computeLinkPath(d));
        // Arrow paths at 1/3 from source → target
        arrowLinks
          .filter(d => visibleGroups.has(d.__groupIndex))
          .attr('d', (d) => arrowPathAtFraction(d, 1 / 3));

        // Update node positions
        node
          .filter(d => visibleGroups.has(d.__groupIndex))
          .attr('transform', d => `translate(${d.x},${d.y})`);

        applyGroupCulling();
        updateUiSurfaceTheme();
      });

      updateUiSurfaceTheme();

      // Drag handlers
      let activeDragNode = null;
      /** True once pointer moved during drag; pure clicks never fire `dragged`. */
      let dragHadMovement = false;

      const releaseActiveDrag = () => {
        // Stop any in-group sim that was sustained during drag before resuming globals.
        stopGroupMiniSimFully();
        // Desktop should always release pinning after drag. On touch we preserve
        // the previous behavior (keep pinned) for direct-manipulation ergonomics.
        if (activeDragNode && !('ontouchstart' in window) && !navigator.maxTouchPoints) {
          activeDragNode.fx = null;
          activeDragNode.fy = null;
        }
        activeDragNode = null;
      };

      handleGlobalDragRelease = () => {
        if (!activeDragNode) return;
        releaseActiveDrag();
      };
      window.addEventListener('mouseup', handleGlobalDragRelease, true);
      window.addEventListener('blur', handleGlobalDragRelease);

      function dragstarted(event, d) {
        dragHadMovement = false;
        // Defensive: if a previous drag ended unexpectedly, clear stale pin.
        if (activeDragNode) {
          releaseActiveDrag();
        }

        activeDragNode = d;
        const gi = groupMap.get(d.id);
        // Pause global sim; run in-group physics for the dragged cluster until drag ends.
        startGroupMiniSim(gi);

        const p = clampNodeToDisk(d.x, d.y);
        d.fx = p.x;
        d.fy = p.y;
      }

      function dragged(event, d) {
        dragHadMovement = true;
        const c = clampNodeToDisk(event.x, event.y);
        d.fx = c.x;
        d.fy = c.y;
      }

      function dragended(event, d) {
        // Only swallow the following click when this was a real drag; tap-to-select
        // must not set this or `click` never runs highlight (see node.on('click')).
        suppressNextClick = dragHadMovement;
        // Ensure we release whichever node is actively tracked even if d differs.
        activeDragNode = activeDragNode || d;
        releaseActiveDrag();
      }

    } catch (error) {
      console.error("Error rendering network visualization:", error);
    }

    return () => {
      if (handleGlobalDragRelease) {
        window.removeEventListener('mouseup', handleGlobalDragRelease, true);
        window.removeEventListener('blur', handleGlobalDragRelease);
      }
      teardownGroupMiniSimOnly();
      if (simulation) simulation.stop();
      if (zoomCleanupRef.current) {
        zoomCleanupRef.current();
        zoomCleanupRef.current = null;
      }
    };
    // Intentionally only `data`: full D3 scene graph is rebuilt when the dataset changes;
    // `colorBy` / `colorMaps` updates are handled by the recolor effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [data]);


  // Lightweight recolor effect

  useEffect(() => {
    if (!svgRef.current || !colorMaps || !data?.nodes?.length) return;

    const groupMap = buildGroupsFromData(data.nodes, data.links ?? []);
    const vals = [...groupMap.values()];
    const groupCount = vals.length ? Math.max(...vals) + 1 : 0;
    const groupSizes = Array.from({ length: groupCount }, () => 0);
    data.nodes.forEach(n => { groupSizes[groupMap.get(n.id)] += 1; });

    const g = d3.select(svgRef.current).select('g');

    g.selectAll('.node').each(function (d) {
      const nodeGroup = d3.select(this);
      const nodePathInfo = createNodePath(d);

      nodeGroup.selectAll('path').remove();
      nodeGroup.selectAll('circle').remove();

      renderNodeVisual(nodeGroup, d, nodePathInfo, {
        colorMaps,
        colorBy,
        getNodeColor,
        includeHub: false,
        includeDataAttrs: true
      });
    });

    // Rebuild cluster contents so the cloud blob's color mix reflects the
    // newly-selected colorBy. Cluster <g>s were created lazily by the main
    // effect for groups with size >= CLUSTER_GROUP_MIN_NODES.
    g.selectAll('.cluster').each(function () {
      const clusterSel = d3.select(this);
      const gi = Number(clusterSel.attr('data-gi'));
      if (!Number.isFinite(gi)) return;
      const groupNodes = data.nodes.filter(n => groupMap.get(n.id) === gi);
      if (!groupNodes.length) return;

      const center = {
        x: groupNodes.reduce((sum, n) => sum + n.x, 0) / groupNodes.length,
        y: groupNodes.reduce((sum, n) => sum + n.y, 0) / groupNodes.length
      };
      const circles = buildColorClusterCircles(groupNodes, getNodeColor, center);
      renderClusterContentsFromModule(clusterSel, circles);
    });
  }, [colorBy, colorMaps, getNodeColor, createNodePath, data]);

  const desktopSafariClass = isDesktopSafariBrowser() ? ' desktop-safari' : '';

  return (
    <div className={`network-container${desktopSafariClass}`}>
      <div className="visualization-area">
        <svg ref={svgRef} className="network-graph"
          aria-label="Network graph visualization - draggable view"></svg>

        <div ref={controlsRef} className="controls-legend-container">
          <ControlPanel
            colorBy={colorBy}
            setColorBy={setColorBy}
            nodes={data.nodes}
            darkSurface={darkSurface}
          />
          <Legend colorBy={colorBy} data={data} darkSurface={darkSurface} />
        </div>
      </div>
    </div>
  );
};

export default NetworkGraph;